// src/services/tickets.js
import api from './api';

const handleApiError = (error, defaultMessage = 'An error occurred') => {
  console.error('API Error:', error);
  
  if (error.response?.data?.error) {
    throw new Error(error.response.data.error);
  } else if (error.response?.data?.message) {
    throw new Error(error.response.data.message);
  } else if (error.response) {
    throw new Error(error.response.statusText || defaultMessage);
  } else if (error.request) {
    throw new Error('Network error: Unable to connect to server');
  } else {
    throw new Error(error.message || defaultMessage);
  }
};

/**
 * Get all tickets
 */
export const getTickets = async (params = {}) => {
  try {
    const response = await api.get('/tickets', { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch tickets');
  }
};

/**
 * Get ticket by ID
 */
export const getTicket = async (ticketId) => {
  try {
    const response = await api.get(`/tickets/${ticketId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch ticket');
  }
};

/**
 * Create new ticket
 */
export const createTicket = async (ticketData) => {
  try {
    const response = await api.post('/tickets', ticketData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to create ticket');
  }
};

/**
 * Add response to ticket
 */
export const addTicketResponse = async (ticketId, responseData) => {
  try {
    const response = await api.post(`/tickets/${ticketId}/responses`, responseData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to add response');
  }
};

/**
 * Update ticket status (Admin only)
 */
export const updateTicketStatus = async (ticketId, status) => {
  try {
    const response = await api.patch(`/tickets/${ticketId}/status`, { status });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to update ticket status');
  }
};

/**
 * Assign ticket (Admin only)
 */
export const assignTicket = async (ticketId, assignedTo) => {
  try {
    const response = await api.patch(`/tickets/${ticketId}/assign`, { assignedTo });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to assign ticket');
  }
};

/**
 * Resolve ticket (Admin only)
 */
export const resolveTicket = async (ticketId, resolutionData) => {
  try {
    const response = await api.post(`/tickets/${ticketId}/resolve`, resolutionData);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to resolve ticket');
  }
};

/**
 * Close ticket
 */
export const closeTicket = async (ticketId) => {
  try {
    const response = await api.patch(`/tickets/${ticketId}/close`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to close ticket');
  }
};

/**
 * Delete ticket (Admin only)
 */
export const deleteTicket = async (ticketId) => {
  try {
    const response = await api.delete(`/tickets/${ticketId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to delete ticket');
  }
};

/**
 * Get ticket statistics (Admin only)
 */
export const getTicketStats = async (params = {}) => {
  try {
    const response = await api.get('/tickets/stats/summary', { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch ticket statistics');
  }
};

export default {
  getTickets,
  getTicket,
  createTicket,
  addTicketResponse,
  updateTicketStatus,
  assignTicket,
  resolveTicket,
  closeTicket,
  deleteTicket,
  getTicketStats
};